import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property, query, queryAll } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { map } from 'lit/directives/map.js'
import { html } from 'lit-element'

import { CommonSize } from '@/common/types'
import DapDSTab from '@/components/tab/tab.component'
import { DdsElement } from '@/internal/dds-hu-element'

import styles from './tab-group.scss?inline'

interface TabNavItem {
  titleTemplate: Node[]
  disabled: boolean
  tabId: string | null
}

/**
 * `dap-ds-tab-group`
 * @summary A tab group component. It is used with dap-ds-tab component to create a tab group.
 * @element dap-ds-tab-group
 * @title - Tab group
 * @group tab
 *
 * @event dds-tab-select - Fired when select a new tab.
 *
 * @slot - The tab items.
 *
 * @csspart base - The main container.
 * @csspart tab-nav - The tab navigation container.
 * @csspart tab-content - The tab content container.
 *
 * @cssproperty --dds-tab-group-border-radius - Border radius of the tab group container (default: var(--dds-radius-base))
 * @cssproperty --dds-tab-border-radius - Border radius for tab items (default: var(--dds-radius-base) var(--dds-radius-base) 0 0)
 * @cssproperty --dds-tab-group-border-width - Border width of the tab navigation (default: var(--dds-border-width-base))
 * @cssproperty --dds-tab-group-border-color - Border color of the tab navigation (default: var(--dds-border-neutral-subtle))
 * @cssproperty --dds-tab-height-sm - Height of small tabs (default: 40px)
 * @cssproperty --dds-tab-height-lg - Height of large tabs (default: 50px)
 * @cssproperty --dds-tab-font-size-sm - Font size of small tabs (default: var(--dds-font-base))
 * @cssproperty --dds-tab-font-size-lg - Font size of large tabs (default: var(--dds-font-lg))
 * @cssproperty --dds-tab-padding - Padding of the tab buttons (default: 0 var(--dds-spacing-400))
 * @cssproperty --dds-tab-border-width - Border width for tabs (default: var(--dds-border-width-large))
 * @cssproperty --dds-tab-selected-border-color - Border color for selected tabs (default: var(--dds-border-brand-base))
 * @cssproperty --dds-tab-text-color - Text color for tabs (default: var(--dds-button-subtle-text-neutral-enabled))
 * @cssproperty --dds-tab-hover-text-color - Text color for tabs on hover (default: var(--dds-text-brand-subtle))
 * @cssproperty --dds-tab-disabled-text-color - Text color for disabled tabs (default: var(--dds-text-neutral-disabled))
 * @cssproperty --dds-tab-hover-background - Background color for tabs on hover (default: var(--dds-transparent-black-subtle))
 * @cssproperty --dds-tab-active-background - Background color for active tabs (default: var(--dds-transparent-black-base))
 */
@customElement('dap-ds-tab-group')
export default class DapDSTabGroup extends DdsElement {
  /**@ignore */
  @query('slot') defaultSlot: HTMLSlotElement
  /**@ignore */
  @queryAll('.tab:not(.tab--disabled)') tabNavElements: HTMLElement[]

  /** The selected tab id. */
  @property({
    type: String,
    reflect: true,
  })
  selectedTabId: string

  /** The size of the tab buttons. Default is `sm`. Can be `sm` or `lg`.
   * @type {'sm' | 'lg'}
   */
  @property({ type: String, reflect: true }) size: CommonSize = 'sm'

  /** If true, the tab group will be displayed in mobile mode. */
  @property({ type: Boolean, reflect: true }) mobile: boolean = false

  static readonly styles = unsafeCSS(styles)

  private _tabNavItems: TabNavItem[] = []
  private _activeTabIds: string[] = []
  private _initialEmit = true

  emitTabSelect(tabId: string) {
    if (!tabId) {
      console.warn('Empty tab id!')
      return
    }

    this.emit('dds-tab-select', { tabId }, { bubbles: false })

    if (this._initialEmit) {
      this._initialEmit = false
    }
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    value: string | null,
  ): void {
    super.attributeChangedCallback(name, oldValue, value)
    if (name.toLocaleLowerCase() === 'selectedtabid' && value !== oldValue) {
      customElements.whenDefined('dap-ds-tab').then(() => {
        if (value && this._activeTabIds.includes(value)) {
          this.emitTabSelect(value)
        }
      })
    }
  }

  handleSlotChange(event: Event) {
    const tabElements: NodeListOf<DapDSTab> = (
      event.target as any
    )?.assignedElements({
      flatten: true,
      selector: 'dap-ds-tab',
    })

    const tabNavItems: TabNavItem[] = []
    const tabElementsList: DapDSTab[] = []
    const activeTabIds: string[] = []

    tabElements.forEach(tab => {
      const tabId = tab.getAttribute('tabId')
      const disabled = tab.hasAttribute('disabled')

      const tabTitle = tab?.shadowRoot?.querySelector(
        'slot:not([name="content"])',
      ) as HTMLSlotElement

      tabNavItems.push({
        titleTemplate: tabTitle?.assignedNodes({ flatten: true }),
        disabled,
        tabId,
      })

      tabElementsList.push(tab)

      if (!disabled && tabId) {
        activeTabIds.push(tabId)
      }
    })

    this._tabNavItems = tabNavItems
    this._activeTabIds = activeTabIds

    if (!this.selectedTabId && this._activeTabIds.length) {
      this.selectedTabId = this._activeTabIds[0]
    } else if (this._initialEmit) {
      this.emitTabSelect(this.selectedTabId)
    }

    this.requestUpdate()
  }

  handleClick(tabId: string | null) {
    if (tabId === this.selectedTabId || !tabId) return

    this.selectedTabId = tabId
    this.emitTabSelect(tabId)
  }

  handleKeyDown(event: KeyboardEvent) {
    const currentTabIndex = this._activeTabIds.indexOf(this.selectedTabId)
    let flag = false

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        {
          if (currentTabIndex > 0) {
            const newTabIndex = currentTabIndex - 1
            this.handleClick(this._activeTabIds[newTabIndex])
            this.tabNavElements[newTabIndex].focus()
          }
          flag = true
        }
        break

      case 'ArrowRight':
      case 'ArrowDown':
        {
          if (currentTabIndex < this._activeTabIds.length - 1) {
            const newTabIndex = currentTabIndex + 1
            this.handleClick(this._activeTabIds[newTabIndex])
            this.tabNavElements[newTabIndex].focus()
          }
          flag = true
        }
        break

      case 'Home':
        this.handleClick(this._activeTabIds[0])
        this.tabNavElements[0].focus()
        flag = true
        break

      case 'End':
        this.handleClick(this._activeTabIds[this._activeTabIds.length - 1])
        this.tabNavElements[this._activeTabIds.length - 1].focus()
        flag = true
        break

      default:
        break
    }

    if (flag) {
      event.stopPropagation()
      event.preventDefault()
    }
  }

  render() {
    return html`
      <div part="base" class="tab-group">
        <div
          role="tablist"
          class="    ${clsx('tab-group__nav', {
            'tab-group--mobile': this.mobile,
          })}">
          ${map(
            this._tabNavItems,
            tab => html`
              <button
                part="tab-nav"
                ?disabled="${tab.disabled}"
                role="tab"
                aria-controls=${ifDefined(tab.tabId ?? undefined)}
                aria-selected="${this.selectedTabId === tab.tabId}"
                tabindex="${this.selectedTabId === tab.tabId ? 0 : -1}"
                type="button"
                class=${clsx('tab', {
                  'tab--selected': this.selectedTabId === tab.tabId,
                  'tab--disabled': tab.disabled,
                  'tab--sm': this.size === 'sm',
                  'tab--lg': this.size === 'lg',
                })}
                @click=${() => {
                  this.handleClick(tab.tabId)
                }}
                @keydown=${this.handleKeyDown}>
                <span
                  part="high-contrast"
                  class=${clsx(
                    'tab__high-contrast',
                    {
                      'tab__high-contrast--selected':
                        this.selectedTabId === tab.tabId,
                    },
                    `tab__high-contrast--${this.size}`,
                  )}></span>
                ${tab.titleTemplate}
              </button>
            `,
          )}
        </div>

        <div part="tab-group-content" class="tab-group__content">
          <slot @slotchange=${this.handleSlotChange}></slot>
        </div>
      </div>
    `
  }
}
